import { Component } from "vue";

import { ProgrammingGanttHeaderColumn } from "@/components/programming-gantt/programming-gantt-header-column";
import { ProgrammingGanttRow } from "@/components/programming-gantt/programming-gantt-row";
import i18n from "@/i18n";
import $date, { EDateScaling } from "@/utils/date";
import { $, createSVG } from "@/utils/selectors";
import { ETimeUnit } from "@/utils/time";

export enum EProgrammingGanttViewMode {
  WEEK = "WEEK",
  MONTH = "MONTH",
  QUARTER = "QUARTER",
}

const COLUMN_WIDTH_PER_VIEW_MODE: { [k in EProgrammingGanttViewMode]: number } = {
  [EProgrammingGanttViewMode.WEEK]: 100,
  [EProgrammingGanttViewMode.MONTH]: 340,
  [EProgrammingGanttViewMode.QUARTER]: 500,
};

export interface IProgrammingGanttRow {
  id: string;
  name: string;
  level?: number;
}

export interface IProgrammingGanttContextAction {
  type: "ACTION";
  id: string;
  label: string;
  icon?: string;
  color?: string;
}

export interface IProgrammingGanttContextDivider {
  type: "DIVIDER";
}

export type TProgrammingGanttContextAction = IProgrammingGanttContextAction | IProgrammingGanttContextDivider;

export interface IProgrammingGanttSchedule {
  id: string;
  rowId: string;
  start: Date;
  end: Date;
  realStart: Date;
  realEnd: Date;
  contextMenu: Array<TProgrammingGanttContextAction>;
}

export interface IProgrammingGanttData<ROW extends IProgrammingGanttRow, SCHEDULE extends IProgrammingGanttSchedule> {
  rows: ROW[];
  schedules: SCHEDULE[];
}

interface IProgrammingGanttBarContentIcon {
  type: "icon";
  icon: string | Component;
}

interface IProgrammingGanttBarContentText {
  type: "text";
  label: string;
}

export type TProgrammingGanttBarContent = IProgrammingGanttBarContentIcon | IProgrammingGanttBarContentText;

export type TProgrammingGanttContextMenuActionClickedEvent<
  ROW extends IProgrammingGanttRow,
  SCHEDULE extends IProgrammingGanttSchedule,
> = {
  row: ROW;
  schedule: SCHEDULE;
  action: string;
};

export interface IProgrammingGanttOptions<
  ROW extends IProgrammingGanttRow,
  SCHEDULE extends IProgrammingGanttSchedule,
> {
  rowHeight: number;
  headerHeight: number;
  allowedViewModes: EProgrammingGanttViewMode[];
  viewMode: EProgrammingGanttViewMode;
  currentDate: Date;
  cameraYPosition?: number;

  formatHeaderDate(date: Date): string;

  formatHeaderMonth(date: Date): string;

  getBarContent(schedule: SCHEDULE, row: ROW): TProgrammingGanttBarContent | undefined;

  getBarBackground(schedule: SCHEDULE, row: ROW): string;

  formatQuarter(date: Date): string;

  onRowSelectionChanged(rows: Array<ROW>): void;

  onContextMenuActionClick(opts: TProgrammingGanttContextMenuActionClickedEvent<ROW, SCHEDULE>): void;

  onChangeViewMode(mode: EProgrammingGanttViewMode): void;
}

const DEFAULT_HEADER_HEIGHT = 65;
const DEFAULT_ROW_HEIGHT = 65;

export const ProgrammingGanttDefaultOptions: IProgrammingGanttOptions<IProgrammingGanttRow, IProgrammingGanttSchedule> =
  Object.freeze({
    headerHeight: DEFAULT_HEADER_HEIGHT,
    rowHeight: DEFAULT_ROW_HEIGHT,
    allowedViewModes: Object.values(EProgrammingGanttViewMode),
    viewMode: EProgrammingGanttViewMode.MONTH,
    currentDate: new Date(),
    formatHeaderDate(date: Date) {
      return `${date.getDate()}`;
    },
    formatHeaderMonth(date: Date) {
      return `${date.getMonth() + 1} ${date.getFullYear()}`;
    },
    getBarBackground() {
      return "green";
    },
    getBarContent() {
      return undefined;
    },
    formatQuarter(date: Date) {
      if (date.getMonth() < 3) {
        return "Q0";
      } else if (date.getMonth() < 6) {
        return "Q1";
      } else if (date.getMonth() < 9) {
        return "Q2";
      }

      return "Q3";
    },
    onRowSelectionChanged() {},
    onContextMenuActionClick() {},
    onChangeViewMode() {},
  });

export class ProgrammingGantt<ROW extends IProgrammingGanttRow, SCHEDULE extends IProgrammingGanttSchedule> {
  private static LEFT_PANEL_WIDTH = 250;

  private $container!: HTMLElement;
  private $svg!: SVGElement;
  private $leftPanelRows!: HTMLElement;
  private $headerRow!: HTMLElement;
  private $rows!: SVGElement;
  private $viewModeIsland!: HTMLElement;
  private $viewModeIslandTodayBtn!: HTMLButtonElement;
  private $viewModeIslandWeekBtn!: HTMLButtonElement;
  private $viewModeIslandMonthBtn!: HTMLButtonElement;
  private $viewModeIslandQuarterBtn!: HTMLButtonElement;

  private $headerColumnPool: Array<ProgrammingGanttHeaderColumn<ROW, SCHEDULE>> = [];
  private ganttRows: Array<ProgrammingGanttRow<ROW, SCHEDULE>> = [];
  private filteredOutRowsIdentifier: Array<ROW["id"]> = [];

  private selectedRowsId: Array<ROW["id"]> = [];

  private schedulePerRow: Record<string, Array<SCHEDULE>> = {};

  private data!: Required<IProgrammingGanttData<ROW, SCHEDULE>>;
  private readonly options: IProgrammingGanttOptions<ROW, SCHEDULE>;
  private readonly initialDate: Date;

  private drag__initialDragX: number = 0;
  private drag__initialDragOffset: number = 0;
  private drag__isDragging: boolean = false;
  private drag__hasDragged: boolean = false;
  private drag__lastDragTime: Date = new Date();

  private cameraPosition: [number, number] = [0, 0];

  constructor(
    wrapper: string | HTMLElement,
    data: IProgrammingGanttData<ROW, SCHEDULE>,
    opts: Partial<IProgrammingGanttOptions<ROW, SCHEDULE>> = {},
  ) {
    this.options = { ...ProgrammingGanttDefaultOptions, ...opts };
    this.initialDate = $date.startOf($date.clone(this.options.currentDate), EDateScaling.DAY);

    this.setupData(data);
    this.setupWrapper(wrapper);
    this.addEventListeners();
    this.translateToDate(this.getInitialDate(), "center");
  }

  public filter(cb: (row: ROW) => boolean, scrollToFirstVisible: boolean = true) {
    this.filteredOutRowsIdentifier = this.data.rows.filter((row) => !cb(row)).map((row) => row.id);

    for (const ganttRow of this.ganttRows) {
      if (this.filteredOutRowsIdentifier.includes(ganttRow.getRow().id)) {
        ganttRow.hide();
      } else {
        ganttRow.show();
      }
    }

    if (scrollToFirstVisible) {
      const filteredRows = this.data.rows.find((row) => !this.filteredOutRowsIdentifier.includes(row.id));
      if (!filteredRows) {
        return;
      }

      const height =
        (filteredRows.level ?? 0) * this.options.headerHeight - this.getViewport().height / 2 + this.options.rowHeight;
      this.$leftPanelRows.scrollTo({
        top: height,
        behavior: "smooth",
      });
    }
  }

  private setupData(data: IProgrammingGanttData<ROW, SCHEDULE>) {
    this.selectedRowsId = [];
    this.schedulePerRow = {} as Record<ROW["id"], SCHEDULE[]>;

    for (const schedule of data.schedules) {
      if (!(schedule.rowId in this.schedulePerRow)) {
        this.schedulePerRow[schedule.rowId as ROW["id"]] = [];
      }

      this.schedulePerRow[schedule.rowId as ROW["id"]].push(schedule);
    }

    this.data = {
      rows: [...data.rows].map((row, idx) => {
        return {
          ...row,
          level: idx,
        };
      }),
      schedules: [...data.schedules],
    };
  }

  public getOptions() {
    return {
      ...this.options,
    };
  }

  public getInitialDate() {
    return $date.startOf($date.clone(this.initialDate), EDateScaling.DAY);
  }

  public getVisibleViewport() {
    return {
      x: this.cameraPosition[0],
      y: this.cameraPosition[1],
      width: this.getViewport().width,
      height: this.getViewport().height,
      right: this.cameraPosition[0] + this.getViewport().width,
      bottom: this.cameraPosition[1] + this.getViewport().height,
    };
  }

  private setupWrapper(wrapper: string | HTMLElement) {
    const wrapperElement = typeof wrapper === "string" ? $(wrapper) : wrapper;

    if (!wrapperElement) {
      throw new Error("Unable to find given wrapper parent element");
    }

    let svgElement: SVGElement | null = null;
    if (wrapperElement instanceof HTMLElement) {
      svgElement = wrapperElement.querySelector("svg");
    } else if (wrapperElement instanceof SVGElement) {
      svgElement = wrapperElement;
    }

    if (!svgElement) {
      this.$svg = createSVG("svg", {
        append_to: wrapperElement,
        class: "gantt",
      });
    } else {
      this.$svg = svgElement;
      this.$svg.classList.add("gantt");
    }

    this.$svg.style.position = "absolute";
    this.$svg.style.left = `${ProgrammingGantt.LEFT_PANEL_WIDTH}px`;
    this.$svg.style.right = "0px";
    this.$svg.style.top = `${this.options.headerHeight}px`;
    this.$svg.style.bottom = "0px";
    $.attr(this.$svg, "width", `calc(100% - ${ProgrammingGantt.LEFT_PANEL_WIDTH}px)`);
    $.attr(this.$svg, "height", `calc(100% - ${this.options.headerHeight}px)`);

    this.$container = document.createElement("div");
    this.$container.classList.add("gantt-container");

    this.$rows = createSVG("g", {
      append_to: this.$svg,
      role: "rows",
    });

    this.cameraPosition = [0, this.options.cameraYPosition ?? 0];

    this.setupLeftPanelRows(this.$container);
    this.setupHeaderRow(this.$container);
    this.setupGanttRows();
    this.setupViewModeIsland(this.$container);

    const parentElement = this.$svg.parentElement!;
    parentElement.appendChild(this.$container);

    this.$container.appendChild(this.$svg);

    // initialize scroll top
    this.$leftPanelRows.scrollTo({ top: this.cameraPosition[1] });
  }

  private setupViewModeIsland(container: HTMLElement) {
    const intl = i18n.global;
    this.$viewModeIsland = document.createElement("div");
    this.$viewModeIsland.classList.add("view-mode-island");
    this.$viewModeIsland.setAttribute("data-test-id", "view-mode-island");

    this.$viewModeIslandTodayBtn = document.createElement("button");
    this.$viewModeIslandTodayBtn.innerHTML = intl.t("global.misc.today");
    this.$viewModeIslandTodayBtn.setAttribute("data-test-id", "today-button");

    const divider = document.createElement("div");
    divider.classList.add("divider");

    this.$viewModeIslandWeekBtn = document.createElement("button");
    this.$viewModeIslandWeekBtn.innerHTML = intl.t(`global.units.temporal.${ETimeUnit.WEEK}`);
    this.$viewModeIslandWeekBtn.setAttribute("data-test-id", "week-mode-button");

    this.$viewModeIslandMonthBtn = document.createElement("button");
    this.$viewModeIslandMonthBtn.innerHTML = intl.t(`global.units.temporal.${ETimeUnit.MONTH}`);
    this.$viewModeIslandMonthBtn.setAttribute("data-test-id", "month-mode-button");

    this.$viewModeIslandQuarterBtn = document.createElement("button");
    this.$viewModeIslandQuarterBtn.innerHTML = intl.t(`global.units.temporal.${ETimeUnit.QUARTER}`);
    this.$viewModeIslandQuarterBtn.setAttribute("data-test-id", "quarter-mode-button");

    if (this.options.viewMode === EProgrammingGanttViewMode.WEEK) {
      this.$viewModeIslandWeekBtn.classList.add("active");
    } else if (this.options.viewMode === EProgrammingGanttViewMode.MONTH) {
      this.$viewModeIslandMonthBtn.classList.add("active");
    } else {
      this.$viewModeIslandQuarterBtn.classList.add("active");
    }

    this.$viewModeIsland.appendChild(this.$viewModeIslandTodayBtn);
    this.$viewModeIsland.appendChild(divider);
    this.$viewModeIsland.appendChild(this.$viewModeIslandWeekBtn);
    this.$viewModeIsland.appendChild(this.$viewModeIslandMonthBtn);
    this.$viewModeIsland.appendChild(this.$viewModeIslandQuarterBtn);
    container.appendChild(this.$viewModeIsland);
  }

  private setupGanttRows() {
    for (const elem of this.ganttRows) {
      elem.destroy();
    }

    this.ganttRows = [];

    for (const row of this.data.rows) {
      const schedules = this.schedulePerRow[row.id] ?? [];

      const ganttRow = new ProgrammingGanttRow(this, row, schedules, this.$rows);

      this.ganttRows.push(ganttRow);
    }
  }

  public getColumnWidth() {
    return COLUMN_WIDTH_PER_VIEW_MODE[this.options.viewMode];
  }

  public isDragging() {
    const now = new Date();
    const timeThreshold = 25;
    const offsetThreshold = 5;

    return (
      this.drag__isDragging ||
      (now.getTime() - this.drag__lastDragTime.getTime() < timeThreshold &&
        Math.abs(this.cameraPosition[0] - this.drag__initialDragOffset) > offsetThreshold)
    );
  }

  private notifyRowSelectionChanged() {
    const rows = this.data.rows.filter((k) => this.selectedRowsId.includes(k.id));
    this.options.onRowSelectionChanged(rows);
  }

  public selectRow(rowId: ROW["id"] | null | undefined, notify: boolean): void;
  public selectRow(rowId: ROW, notify: boolean): void;
  public selectRow(row?: unknown | null, notify: boolean = true) {
    if (!row) {
      return;
    }

    let rowId!: string;
    if (typeof row === "object" && "id" in row) {
      rowId = (row as ROW)["id"];
    } else if (typeof row === "string") {
      rowId = row;
    }

    const rowElement = document.querySelector(`.gantt-left-panel__row[row-id="${rowId}"]`) as HTMLElement;
    const checkboxElement = document.querySelector(
      `.gantt-left-panel__row[row-id="${rowId}"] input[type="checkbox"]`,
    ) as HTMLInputElement;
    if (!rowElement || !checkboxElement) {
      return;
    }

    if (this.selectedRowsId.includes(rowId)) {
      return;
    }

    this.selectedRowsId.push(rowId);
    checkboxElement.checked = true;
    rowElement.classList.add("selected");
    $.attr(rowElement, "selected", "true");

    if (notify) {
      this.notifyRowSelectionChanged();
    }
  }

  public unselectRow(rowId: ROW["id"] | null | undefined, notify: boolean): void;
  public unselectRow(row: ROW | null | undefined, notify: boolean): void;
  public unselectRow(row?: unknown | null, notify: boolean = true) {
    if (!row) {
      return;
    }

    let rowId!: string;
    if (typeof row === "object" && "id" in row) {
      rowId = (row as ROW)["id"];
    } else if (typeof row === "string") {
      rowId = row;
    }

    const rowElement = document.querySelector(`.gantt-left-panel__row[row-id="${rowId}"]`) as HTMLElement;
    const checkboxElement = document.querySelector(
      `.gantt-left-panel__row[row-id="${rowId}"] input[type="checkbox"]`,
    ) as HTMLInputElement;
    if (!rowElement || !checkboxElement) {
      return;
    }

    if (!this.selectedRowsId.includes(rowId)) {
      return;
    }

    this.selectedRowsId = this.selectedRowsId.filter((k) => k !== rowId);
    checkboxElement.checked = false;
    rowElement.classList.remove("selected");
    $.attr(rowElement, "selected", "false");

    if (notify) {
      this.notifyRowSelectionChanged();
    }
  }

  public selectAll() {
    this.data.rows.forEach((row) => this.selectRow(row.id, false));
    this.notifyRowSelectionChanged();
  }

  public unselectAll() {
    this.data.rows.forEach((row) => this.unselectRow(row.id, false));
    this.notifyRowSelectionChanged();
  }

  public toggleRowSelection(rowId: ROW["id"] | null | undefined): void;
  public toggleRowSelection(row: ROW | null | undefined): void;
  public toggleRowSelection(row?: unknown | null) {
    if (!row) {
      return;
    }

    let rowId!: string;
    if (typeof row === "object" && "id" in row) {
      rowId = (row as ROW)["id"];
    } else if (typeof row === "string") {
      rowId = row;
    }

    if (this.selectedRowsId.includes(rowId)) {
      this.unselectRow(rowId, true);
    } else {
      this.selectRow(rowId, true);
    }
  }

  private getDateByPosition(x: number) {
    switch (this.options.viewMode) {
      case EProgrammingGanttViewMode.WEEK: {
        const k = x / this.getColumnWidth();
        return $date.subtract(this.getInitialDate(), k, EDateScaling.DAY);
      }
      case EProgrammingGanttViewMode.MONTH: {
        const k = x / this.getColumnWidth();
        const kDecimal = k % 1;

        const month = $date.subtract(this.getInitialDate(), k, EDateScaling.MONTHS);

        const numberOfDays = $date.daysInMonth(month);
        const day = Math.floor(numberOfDays * kDecimal);

        return $date.add(month, day, EDateScaling.DAYS);
      }
      case EProgrammingGanttViewMode.QUARTER: {
        const k = x / this.getColumnWidth();
        const kDecimal = k % 1;

        const quarter = $date.subtract(this.getInitialDate(), k, EDateScaling.QUARTER);

        const numberOfDays = $date.daysInQuarter(quarter);
        const day = Math.floor(numberOfDays * kDecimal);

        return $date.add(quarter, day, EDateScaling.DAYS);
      }
      default:
        return new Date();
    }
  }

  private handleWindowResizeHeaderRow() {
    const currentNumberOfColumns = this.getNumberOfColumnsInViewport() + 1;

    if (currentNumberOfColumns === this.$headerColumnPool.length) {
      return; // Same amount, no need to shrink / expend columns
    }

    const sorted = this.getHeaderColumnsSortedByPosition();
    if (currentNumberOfColumns < this.$headerColumnPool.length) {
      const column = sorted.pop();

      if (!column) {
        return;
      }

      this.$headerColumnPool = this.$headerColumnPool.filter((k) => k !== column);
      column.container().remove();
    } else {
      const column = sorted.pop();
      if (!column) {
        return;
      }

      const currentDate = this.generateNextDate(column.getDate());

      const newColumn = new ProgrammingGanttHeaderColumn(
        this,
        column.getX() + this.getColumnWidth(),
        currentDate,
        this.generateIndexFromDate(currentDate),
      );
      this.$headerRow.appendChild(newColumn.container());
      this.$headerColumnPool.push(newColumn);
    }
  }

  private setupHeaderRow(container: HTMLElement) {
    for (const headerColumn of this.$headerColumnPool) {
      headerColumn.container().remove();
    }

    this.$headerRow?.remove();

    this.$headerColumnPool = [];
    this.$headerRow = document.createElement("div");
    this.$headerRow.classList.add("gantt-header");
    this.$headerRow.style.setProperty("--left-panel-width", `${ProgrammingGantt.LEFT_PANEL_WIDTH}px`);
    this.$headerRow.style.setProperty("--header-height", `${this.options.headerHeight}px`);

    let currentDate = this.getDateByPosition(this.cameraPosition[0]);

    for (let i = 0; i < this.getNumberOfColumnsInViewport(); i++) {
      let x = this.getDateXPosition(currentDate);
      if (this.options.viewMode === EProgrammingGanttViewMode.MONTH) {
        const daysDiff = $date.diff(currentDate, $date.startOf(currentDate, EDateScaling.MONTH), EDateScaling.DAYS);
        x -= (daysDiff * this.getColumnWidth()) / $date.daysInMonth(currentDate);
      } else if (this.options.viewMode === EProgrammingGanttViewMode.QUARTER) {
        const daysDiff = $date.diff(currentDate, $date.startOf(currentDate, EDateScaling.QUARTER), EDateScaling.DAYS);
        x -= (daysDiff * this.getColumnWidth()) / $date.daysInQuarter(currentDate);
      }

      const column = new ProgrammingGanttHeaderColumn(this, x, currentDate, this.generateIndexFromDate(currentDate));
      this.$headerRow.appendChild(column.container());
      this.$headerColumnPool.push(column);

      currentDate = this.generateNextDate(currentDate);
    }

    container.appendChild(this.$headerRow);
  }

  private setupLeftPanelRows(container: HTMLElement) {
    const leftPanel = document.createElement("div");
    leftPanel.classList.add("gantt-left-panel");
    leftPanel.style.setProperty("--header-height", `${this.options.headerHeight}px`);
    leftPanel.style.width = `${ProgrammingGantt.LEFT_PANEL_WIDTH}px`;

    this.$leftPanelRows = document.createElement("div");
    this.$leftPanelRows.style.setProperty("--header-height", `${this.options.headerHeight}px`);
    this.$leftPanelRows.classList.add("gantt-left-panel__row-container");
    this.$leftPanelRows.classList.add("pb-16");

    for (const row of this.data.rows) {
      const rowElement = document.createElement("div");
      rowElement.classList.add("gantt-left-panel__row");
      rowElement.setAttribute("data-test-id", "left-panel-row");
      rowElement.role = "button";
      rowElement.style.setProperty("--header-height", `${this.options.headerHeight}px`);
      $.attr(rowElement, "row-id", row.id);

      const input = document.createElement("input");
      input.type = "checkbox";
      input.classList.add("p-checkbox", "p-component");
      input.addEventListener("keypress", function (e) {
        if (e.key === "Enter") {
          input.click();
        }
      });

      const label = document.createElement("span");
      label.innerHTML = row.name;

      rowElement.appendChild(input);
      rowElement.appendChild(label);

      this.$leftPanelRows.appendChild(rowElement);
    }

    leftPanel.appendChild(this.$leftPanelRows);
    container.appendChild(leftPanel);
  }

  private generateNextDate(date: Date) {
    switch (this.options.viewMode) {
      case EProgrammingGanttViewMode.WEEK:
        return $date.add(date, 1, EDateScaling.DAY);
      case EProgrammingGanttViewMode.MONTH:
        return $date.add(date, 1, EDateScaling.MONTH);
      case EProgrammingGanttViewMode.QUARTER: {
        return $date.add(date, 1, EDateScaling.QUARTER);
      }
    }
  }

  private generatePreviousDate(date: Date) {
    switch (this.options.viewMode) {
      case EProgrammingGanttViewMode.WEEK:
        return $date.subtract(date, 1, EDateScaling.DAY);
      case EProgrammingGanttViewMode.MONTH:
        return $date.subtract(date, 1, EDateScaling.MONTH);
      case EProgrammingGanttViewMode.QUARTER: {
        return $date.subtract(date, 1, EDateScaling.QUARTER);
      }
    }
  }

  private generateIndexFromDate(date: Date) {
    switch (this.options.viewMode) {
      case EProgrammingGanttViewMode.WEEK:
        return $date.diff(
          $date.startOf(this.getInitialDate(), EDateScaling.MONTH),
          $date.startOf(date, EDateScaling.MONTH),
          EDateScaling.MONTH,
        );
      case EProgrammingGanttViewMode.MONTH:
        return $date.diff(
          $date.startOf(this.getInitialDate(), EDateScaling.MONTH),
          $date.startOf(date, EDateScaling.MONTH),
          EDateScaling.MONTH,
        );
      case EProgrammingGanttViewMode.QUARTER:
        return $date.diff(
          $date.startOf(this.getInitialDate(), EDateScaling.QUARTER),
          $date.startOf(date, EDateScaling.QUARTER),
          EDateScaling.QUARTER,
        );
    }
  }

  private addEventListeners() {
    this.$svg.addEventListener("mousedown", this.onSvgMouseDown);
    window.addEventListener("mouseup", this.onSvgMouseUp);
    window.addEventListener("mousemove", this.onSvgMouseMove);

    this.$leftPanelRows.addEventListener("scroll", this.onLeftPanelRowsScroll);

    document.querySelectorAll(".gantt-left-panel__row").forEach((row) => {
      const rowHtmlElement = row as HTMLElement;
      rowHtmlElement.addEventListener("mouseenter", () => {
        const rowId = $.attr(rowHtmlElement, "row-id");
        document.querySelectorAll(`.gantt-row[row-id="${rowId}"]`).forEach((ganttRow) => {
          ganttRow.classList.add("highlight");
        });
      });

      rowHtmlElement.addEventListener("mouseleave", () => {
        const rowId = $.attr(rowHtmlElement, "row-id");
        document.querySelectorAll(`.gantt-row[row-id="${rowId}"]`).forEach((ganttRow) => {
          ganttRow.classList.remove("highlight");
        });
      });

      rowHtmlElement.addEventListener("click", () => {
        const rowId = $.attr(rowHtmlElement, "row-id");
        this.toggleRowSelection(rowId);
      });
    });

    this.$viewModeIslandTodayBtn.addEventListener("click", this.onTodayBtnClicked);
    this.$viewModeIslandWeekBtn.addEventListener("click", () => this.changeViewMode(EProgrammingGanttViewMode.WEEK));
    this.$viewModeIslandMonthBtn.addEventListener("click", () => this.changeViewMode(EProgrammingGanttViewMode.MONTH));
    this.$viewModeIslandQuarterBtn.addEventListener("click", () =>
      this.changeViewMode(EProgrammingGanttViewMode.QUARTER),
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).RetargetMouseScroll(this.$svg, this.$leftPanelRows, true, 8);

    window.addEventListener("resize", this.onWindowResize);
  }

  public destroy() {
    window.removeEventListener("mouseup", this.onSvgMouseUp);
    window.removeEventListener("mousemove", this.onSvgMouseMove);
    window.removeEventListener("resize", this.onWindowResize);

    this.$container?.remove();
  }

  private onWindowResize = () => {
    this.handleWindowResizeHeaderRow();
    this.handlePostDragHorizontal("left");
  };

  public changeViewMode(nextViewMode: EProgrammingGanttViewMode) {
    if (this.options.viewMode === nextViewMode) {
      return;
    }

    this.options.viewMode = nextViewMode;
    [this.$viewModeIslandWeekBtn, this.$viewModeIslandMonthBtn, this.$viewModeIslandQuarterBtn].forEach((btn) => {
      btn.classList.remove("active");
    });

    switch (nextViewMode) {
      case EProgrammingGanttViewMode.WEEK:
        this.$viewModeIslandWeekBtn.classList.add("active");
        break;
      case EProgrammingGanttViewMode.MONTH:
        this.$viewModeIslandMonthBtn.classList.add("active");
        break;
      case EProgrammingGanttViewMode.QUARTER:
        this.$viewModeIslandQuarterBtn.classList.add("active");
        break;
    }

    this.cameraPosition = [0, this.cameraPosition[1]];
    this.setupHeaderRow(this.$container);
    this.setupGanttRows();
    this.translateToDate(this.getInitialDate(), "center");

    this.$leftPanelRows.scrollTo({ top: this.cameraPosition[1] });
    this.handlePostScroll();

    this.options.onChangeViewMode(nextViewMode);
  }

  private onTodayBtnClicked = () => {
    this.cameraPosition = [0, this.cameraPosition[1]];
    this.setupHeaderRow(this.$container);
    this.setupGanttRows();
    this.translateToDate(this.getInitialDate(), "center");

    this.$leftPanelRows.scrollTo({ top: this.cameraPosition[1] });
    this.handlePostScroll();
  };

  private onLeftPanelRowsScroll = (event: Event) => {
    event.stopImmediatePropagation();
    event.preventDefault();

    this.cameraPosition = [this.cameraPosition[0], this.$leftPanelRows.scrollTop];
    this.handlePostScroll();
  };

  private onSvgMouseDown = (event: MouseEvent) => {
    if (event.button !== 0) {
      return;
    }

    this.drag__initialDragX = event.x;
    this.drag__initialDragOffset = this.cameraPosition[0];
    this.drag__isDragging = true;
    this.drag__hasDragged = false;
    window.document.documentElement.classList.add("dragging");
  };

  public getDateXPosition(date: Date) {
    switch (this.options.viewMode) {
      case EProgrammingGanttViewMode.WEEK: {
        // Calcul de l'offset en fonction du nombre de jours depuis la date initiale
        const numberOfDays = $date.diff(date, this.getInitialDate(), EDateScaling.DAYS);

        const xOffset = numberOfDays * this.getColumnWidth();

        // Calcul de la position relative dans la journée
        const minDate = $date.startOf(date, EDateScaling.DAY);

        const totalNumberOfHours = 24; // Une journée a 24 heures
        const currentNumberOfHours = $date.diff(date, minDate, EDateScaling.HOURS);

        // Position relative dans la journée
        const positionInDay = (currentNumberOfHours / totalNumberOfHours) * this.getColumnWidth();

        return xOffset + positionInDay;
      }

      case EProgrammingGanttViewMode.MONTH: {
        const numberOfMonth = $date.diff(date, this.getInitialDate(), EDateScaling.MONTH);

        const xOffset = numberOfMonth * this.getColumnWidth();

        const minDate = $date.startOf(date, EDateScaling.MONTH);

        const totalNumberOfHours = 24 * $date.daysInMonth(minDate);
        const currentNumberOfHours = $date.diff(date, minDate, EDateScaling.HOURS);

        const positionInDay = (currentNumberOfHours / totalNumberOfHours) * this.getColumnWidth();

        return xOffset + positionInDay;
      }

      case EProgrammingGanttViewMode.QUARTER: {
        const numberOfQuarters = $date.diff(date, this.getInitialDate(), EDateScaling.QUARTERS);
        const xOffset = numberOfQuarters * this.getColumnWidth();

        const minDate = $date.startOf(date, EDateScaling.QUARTER);
        const totalNumberOfHours = 24 * $date.daysInQuarter(minDate);
        const currentNumberOfHours = $date.diff(date, minDate, EDateScaling.HOURS);

        const positionInQuarter = (currentNumberOfHours / totalNumberOfHours) * this.getColumnWidth();

        return xOffset + positionInQuarter;
      }
      default:
        return 0;
    }
  }

  private onSvgMouseUp = (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (this.drag__hasDragged) {
      this.drag__lastDragTime = new Date();
    }

    this.drag__isDragging = false;
    this.drag__hasDragged = false;
    this.$svg.classList.remove("dragging");

    window.document.documentElement.classList.remove("dragging");
  };

  private onSvgMouseMove = (event: MouseEvent) => {
    if (!this.drag__isDragging) {
      return;
    }

    this.$svg.classList.add("dragging");

    const previousCameraX = this.cameraPosition[0];
    this.cameraPosition = [this.drag__initialDragOffset - (event.x - this.drag__initialDragX), this.cameraPosition[1]];

    if (previousCameraX !== this.cameraPosition[0]) {
      const direction = previousCameraX < this.cameraPosition[0] ? "right" : "left";
      this.drag__hasDragged = true;
      this.handlePostDragHorizontal(direction);
    }
  };

  private translateToDate(date: Date, position: "center" | "left" | "right" = "center") {
    const previousCameraPosition = this.cameraPosition[0];

    switch (this.options.viewMode) {
      case EProgrammingGanttViewMode.WEEK: {
        const daysDiff = $date.diff($date.startOf(date, EDateScaling.DAY), this.getInitialDate(), EDateScaling.DAYS);

        let offset!: number;
        if (position === "left") {
          offset = daysDiff * this.getColumnWidth();
        } else if (position === "right") {
          offset = daysDiff * this.getColumnWidth() - this.getVisibleViewport().width + this.getColumnWidth();
        } else {
          offset = daysDiff * this.getColumnWidth() - this.getVisibleViewport().width / 2;
        }
        this.cameraPosition = [offset, this.cameraPosition[1]];
        break;
      }
      case EProgrammingGanttViewMode.MONTH: {
        const monthDiff = $date.diff(
          $date.startOf(date, EDateScaling.MONTH),
          $date.startOf(this.getInitialDate(), EDateScaling.MONTH),
          EDateScaling.MONTH,
        );

        let offset!: number;
        if (position === "left") {
          offset = monthDiff * this.getColumnWidth();
        } else if (position === "right") {
          offset = monthDiff * this.getColumnWidth() - this.getVisibleViewport().width + this.getColumnWidth();
        } else {
          offset = monthDiff * this.getColumnWidth() - this.getVisibleViewport().width / 2 + this.getColumnWidth() / 2;
        }
        this.cameraPosition = [offset, this.cameraPosition[1]];
        break;
      }
    }

    this.handlePostDragHorizontal(previousCameraPosition < this.cameraPosition[0] ? "right" : "left");
  }

  private getHeaderColumnsSortedByPosition() {
    return [...this.$headerColumnPool].sort((a, b) => a.getX() - b.getX());
  }

  private getRightestHeaderColumn() {
    return this.getHeaderColumnsSortedByPosition().pop()!;
  }

  private getLeftestHeaderColumn() {
    return this.getHeaderColumnsSortedByPosition().shift()!;
  }

  private handlePostScroll() {
    this.ganttRows.forEach((row) => {
      row.moveYBy(this.cameraPosition[1]);
    });
  }

  private handlePostDragHorizontal(direction: "left" | "right") {
    this.$headerColumnPool.forEach((column) => {
      column.moveFromOriginBy(-this.cameraPosition[0]);
    });

    this.ganttRows.forEach((row) => {
      row.moveXBy(-this.cameraPosition[0]);
    });

    const poolLength = this.$headerColumnPool.length;

    if (direction === "right") {
      for (let i = 0; i < poolLength; i++) {
        const column = this.$headerColumnPool[i];

        if (column.isTooFarInLeftSide()) {
          const rightestColumn = this.getRightestHeaderColumn();
          const nextDate = this.generateNextDate(rightestColumn.getDate());

          column.moveTo(rightestColumn.getX() + this.getColumnWidth());
          column.changeDate(nextDate);
          column.changeIndex(this.generateIndexFromDate(nextDate));
        }
      }
    }
    if (direction === "left") {
      for (let i = poolLength - 1; i >= 0; i--) {
        const column = this.$headerColumnPool[i];

        if (column.isTooFarInRightSide()) {
          const leftestColumn = this.getLeftestHeaderColumn();
          const previousDate = this.generatePreviousDate(leftestColumn.getDate());

          column.moveTo(leftestColumn.getX() - this.getColumnWidth());
          column.changeDate(previousDate);
          column.changeIndex(this.generateIndexFromDate(previousDate));
        }
      }
    }
  }

  private getNumberOfColumnsInViewport() {
    // We accept to have 2 columns just to avoid having column popping from nowhere
    return Math.floor(this.getViewport().width / this.getColumnWidth()) + 2;
  }

  private getViewport() {
    return {
      width: this.$svg.clientWidth,
      height: this.$svg.clientHeight,
    };
  }

  public getViewMode() {
    return this.options.viewMode;
  }

  public getRowHeight() {
    return this.options.rowHeight;
  }

  public isRowVisible(row: ROW) {
    return !this.filteredOutRowsIdentifier.includes(row.id);
  }

  public contextMenuItemClicked(row: ROW, schedule: SCHEDULE, id: string) {
    this.options.onContextMenuActionClick({
      row: row,
      schedule: schedule,
      action: id,
    });
  }
}
